import { useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import type { WorkflowBlock } from '@/types/workflow';
import { dispatchOnboardingEvent } from '@/components/onboarding/uiAdapter';

export interface UseBlockHandlersDeps {
  blocks: WorkflowBlock[];
  selectedBlockId: string | null;
  setSelectedBlockId: (id: string | null) => void;
  setSelectedGroupId: (id: string | null) => void;
  /** Selection-mutex: välja ett block rensar connector-highlight + panel. */
  setSelectedConnectorId: (id: string | null) => void;
  deleteBlock: (id: string) => void;
  updateBlock: (id: string, patch: Partial<WorkflowBlock>) => void;
  convertBlockToLayer: (id: string) => void;
  logAction: (action: string, detail?: string) => void;
  announce: (msg: string) => void;
  t: (key: string) => string;
}

export function useBlockHandlers(deps: UseBlockHandlersDeps) {
  const {
    blocks, selectedBlockId, setSelectedBlockId, setSelectedGroupId,
    setSelectedConnectorId,
    deleteBlock, updateBlock, convertBlockToLayer,
    logAction, announce, t,
  } = deps;

  const handleSelectBlock = useCallback((id: string) => {
    setSelectedBlockId(id);
    setSelectedGroupId(null);
    // Connector-selection lever i SelectionModeContext och dess setter
    // rensar bara connector ↔ group. När user väljer ett block måste
    // call-site rensa connector-selection explicit, annars stannar
    // PropertiesPanel kvar i connector-vy (prio: connector > block).
    setSelectedConnectorId(null);
  }, [setSelectedBlockId, setSelectedGroupId, setSelectedConnectorId]);

  const handleDeleteSelected = useCallback(() => {
    if (!selectedBlockId) return;
    const blockName = blocks.find(b => b.id === selectedBlockId)?.config.name || 'Block';
    deleteBlock(selectedBlockId);
    logAction('Block deleted', blockName);
    announce(`${blockName} deleted`);
    requestAnimationFrame(() => {
      const nextBlock = document.querySelector('[data-block-id]') as HTMLElement | null;
      if (nextBlock) nextBlock.focus();
      else document.getElementById('canvas')?.focus();
    });
  }, [selectedBlockId, blocks, deleteBlock, logAction, announce]);

  const handleUpdateBlockIcon = useCallback((id: string, iconKey: string) => {
    const existing = blocks.find(b => b.id === id);
    updateBlock(id, { config: { name: '', ...(existing ? existing.config : {}), icon: iconKey } });
  }, [updateBlock, blocks]);

  const handleUpdateBlockColor = useCallback((id: string, color: string) => {
    updateBlock(id, { color });
  }, [updateBlock]);

  const handleConvertToLayer = useCallback((blockId: string) => {
    convertBlockToLayer(blockId);
    toast({
      title: t('layerCreated') || 'Lager skapat',
      description: t('layerCreatedDesc') || 'Blocket har konverterats till ett lager. Dubbelklicka för att öppna.',
    });
    dispatchOnboardingEvent({ name: 'block:convertedToLayer', target: blockId });
  }, [convertBlockToLayer, t]);

  return {
    handleSelectBlock,
    handleDeleteSelected,
    handleUpdateBlockIcon,
    handleUpdateBlockColor,
    handleConvertToLayer,
  };
}
